import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { TextLayer } from 'pdfjs-dist';
import { PDFDocument, rgb, StandardFonts, degrees } from 'pdf-lib';
import { IonSpinner, IonIcon } from '@ionic/react';
import {
  alertCircleOutline,
  downloadOutline,
  printOutline,
  shareOutline,
  addOutline,
  removeOutline,
  gridOutline,
  closeOutline,
  contractOutline,
  expandOutline,
  refreshOutline,
  handLeftOutline,
  textOutline,
  pencilOutline,
  chatbubbleOutline,
  closeCircleOutline,
  arrowUndoOutline,
  ellipsisHorizontal,
} from 'ionicons/icons';
import 'pdfjs-dist/web/pdf_viewer.css';
import { getPdfWorkerBlobUrl } from '../utils/pdfWorkerBlob';
import styles from './PdfViewerNative.module.scss';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

pdfjsLib.GlobalWorkerOptions.workerSrc = getPdfWorkerBlobUrl();

interface PdfViewerNativeProps {
  dataUrl: string | null;
  isLoading: boolean;
  fileName?: string;
  onZoomChange?: (zoom: number) => void;
  status?: string;
}

const STATUS_RIBBON: Record<string, { label: string; r: number; g: number; b: number }> = {
  sent:           { get label() { return i18n.t('components.PdfViewerNative.sent'); },     r: 0.15, g: 0.48, b: 0.94 },
  viewed:         { get label() { return i18n.t('components.PdfViewerNative.viewed'); },   r: 0.15, g: 0.48, b: 0.94 },
  draft:          { get label() { return i18n.t('components.PdfViewerNative.draft'); },    r: 0.55, g: 0.55, b: 0.60 },
  overdue:        { get label() { return i18n.t('components.PdfViewerNative.overdue'); },  r: 0.88, g: 0.22, b: 0.22 },
  partially_paid: { get label() { return i18n.t('components.PdfViewerNative.partial'); },  r: 0.93, g: 0.47, b: 0.07 },
  void:           { get label() { return i18n.t('components.PdfViewerNative.void'); },     r: 0.38, g: 0.38, b: 0.40 },
  unpaid:         { get label() { return i18n.t('components.PdfViewerNative.unpaid'); },   r: 0.35, g: 0.55, b: 0.78 },
};

async function addStatusRibbon(pdfData: ArrayBuffer | Uint8Array, status: string): Promise<Uint8Array | ArrayBuffer | null> {
  const config = STATUS_RIBBON[status];
  if (!config) return null;
  try {
    const doc = await PDFDocument.load(pdfData);
    const page = doc.getPages()[0];
    if (!page) return null;
    const { height } = page.getSize();
    const font = await doc.embedFont(StandardFonts.HelveticaBold);

    // Diagonal ribbon across the top-left corner at 45°.
    // Rectangle: origin (-8, h-78), width 105 goes along 45°, height 22 goes perpendicular (135°).
    // Ribbon center in page coords: (-8+(52.5-11)/√2,  h-78+(52.5+11)/√2) ≈ (21.35, h-33.1)
    page.drawRectangle({
      x: -8,
      y: height - 78,
      width: 105,
      height: 22,
      color: rgb(config.r, config.g, config.b),
      rotate: degrees(45),
      opacity: 0.93,
    });
    // Center the text: baseline start = ribbon_center − half_text_length along 45°
    // Center line: y = x + height − 54.44  →  xt = 21.35 − textW/(2√2)
    const textW = config.label.length * 5.6; // approx advance (Helvetica Bold 8pt)
    const xt = 21.35 - textW / (2 * Math.SQRT2);
    const yt = xt + height - 54.44;
    page.drawText(config.label, {
      x: xt,
      y: yt,
      size: 8,
      font,
      color: rgb(1, 1, 1),
      rotate: degrees(45),
    });

    return await doc.save();
  } catch {
    return null;
  }
}

const MIN_ZOOM = 25;
const MAX_ZOOM = 400;
const ZOOM_STEP = 15;
// Highlight colors: vivid for the swatch buttons, applied with multiply blend on the page
const HL_COLORS = [
  'rgba(255, 213, 0, 0.42)',   // yellow
  'rgba(0,   200, 80, 0.35)',  // green
  'rgba(255,  70, 70, 0.32)',  // red
  'rgba(0,   140, 255, 0.32)', // blue
];

// Group rects that share the same visual line (midpoints within 35% of height).
function mergeRects(raw: Array<{ x: number; y: number; w: number; h: number }>) {
  if (raw.length === 0) return [];
  const sorted = [...raw].sort((a, b) => a.y - b.y);
  const rows: Array<typeof raw> = [];
  for (const r of sorted) {
    const midY = r.y + r.h / 2;
    const row = rows.find(rw => Math.abs(rw[0].y + rw[0].h / 2 - midY) < r.h * 0.35);
    if (row) row.push(r); else rows.push([r]);
  }
  return rows.map(row => ({
    x: Math.min(...row.map(r => r.x)),
    y: Math.min(...row.map(r => r.y)),
    w: Math.max(...row.map(r => r.x + r.w)) - Math.min(...row.map(r => r.x)),
    h: Math.max(...row.map(r => r.h)),
  }));
}

interface Highlight {
  id: string;
  pageNum: number;
  rects: Array<{ x: number; y: number; w: number; h: number }>;
  color: string;
  zoomAtSave: number;
}

interface TextNote {
  id: string;
  pageNum: number;
  x: number;
  y: number;
  width: number;
  height: number;
  text: string;
  editing: boolean;
  zoomAtSave: number;
}

const PdfViewerNative: React.FC<PdfViewerNativeProps> = ({
  dataUrl,
  isLoading,
  fileName = 'document.pdf',
  onZoomChange,
  status,
}) => {
  const { t } = useTranslation();
  const [pdfDoc, setPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  // Natural (100%, untransformed) pixel size of .pagesContainer — drives the
  // zoomWrapper's explicit width/height below, which is what gives
  // `.content`'s overflow:auto a correctly-sized scrollable region even
  // though the actual content is only ever CSS-transformed, never resized.
  const [naturalSize, setNaturalSize] = useState<{ width: number; height: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showThumbnails, setShowThumbnails] = useState(false);
  const [thumbnails, setThumbnails] = useState<{ [key: number]: string }>({});
  const [renderedPages, setRenderedPages] = useState<Set<number>>(new Set());
  const [isDragging, setIsDragging] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [tbWidth, setTbWidth] = useState(800);
  const [viewMode, setViewMode] = useState<'select' | 'highlight' | 'pan' | 'note'>('select');
  const [highlights, setHighlights] = useState<Highlight[]>([]);
  const [hlDrag, setHlDrag] = useState<{ pageNum: number; x: number; y: number; w: number; h: number } | null>(null);
  const hlDragStartRef = useRef<{ pageNum: number; x: number; y: number } | null>(null);
  // Always-current pointer to applyHighlightFromDrag (declared later in this
  // component) — the touch effect below needs to call it, but can't put it
  // directly in that effect's own dependency array without a forward
  // reference at module-init time. A ref synced right after the callback's
  // real declaration sidesteps that ordering problem (same pattern as
  // zoomRef elsewhere in this file).
  const applyHighlightFromDragRef = useRef<((pageNum: number, drag: { x: number; y: number; w: number; h: number }) => void) | null>(null);
  const [activeHlColor, setActiveHlColor] = useState(HL_COLORS[0]);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [toolsMenuOpen, setToolsMenuOpen] = useState(false);
  const [notes, setNotes] = useState<TextNote[]>([]);
  const [annotationHistory, setAnnotationHistory] = useState<Array<{ type: 'highlight' | 'note'; id: string }>>([]);

  const outerContainerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const pageInputRef = useRef<HTMLInputElement>(null);
  const canvasRefs = useRef<{ [key: number]: HTMLCanvasElement | null }>({});
  const textLayerDivRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const textLayerInstanceRefs = useRef<{ [key: number]: { cancel: () => void } | null }>({});
  const textContentCacheRef = useRef<{ [key: number]: any }>({});
  const annotationLayerDivRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const pageContainerRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const wheelDeltaRef = useRef(0);
  const pinchDistRef = useRef<number | null>(null);
  const pinchStartZoomRef = useRef(100);
  const pagesContainerRef = useRef<HTMLDivElement>(null);
  const zoomRef = useRef(100);
  const touchPanRef = useRef<{ x: number; y: number } | null>(null);
  const pendingPinchAnchorRef = useRef<{ contentX: number; contentY: number; localX: number; localY: number; ratio: number } | null>(null);
  const noteDragRef = useRef<{
    id: string;
    startMouseX: number; startMouseY: number;
    startNoteX: number;  startNoteY: number;
    scale: number;
  } | null>(null);
  const renderTasksRef = useRef<{ [key: number]: ReturnType<pdfjsLib.PDFPageProxy['render']> | null }>({});

  // ── Load PDF + fit-to-width ────────────────────────────────────────────────
  useEffect(() => {
    if (!dataUrl) return;
    const loadPdf = async () => {
      try {
        console.log('[PDFViewer] A. loadPdf start, dataUrl prefix:', dataUrl.slice(0, 40));
        setError(null);
        setCurrentPage(1);
        setPdfDoc(null);
        setRenderedPages(new Set());
        setHighlights([]);

        let pdfData: ArrayBuffer | Uint8Array;
        if (dataUrl.startsWith('data:application/pdf;base64,')) {
          const b64 = dataUrl.replace('data:application/pdf;base64,', '');
          console.log('[PDFViewer] B. b64 string length:', b64.length, '(~', Math.round(b64.length * 0.75 / 1024), 'KB decoded)');
          const bin = atob(b64);
          console.log('[PDFViewer] C. atob done, bin.length:', bin.length);
          const bytes = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
          pdfData = bytes.buffer;
          console.log('[PDFViewer] D. Uint8Array built, byteLength:', pdfData.byteLength);
        } else {
          pdfData = await fetch(dataUrl).then(r => r.arrayBuffer());
          console.log('[PDFViewer] D. fetch done, byteLength:', pdfData.byteLength);
        }

        if (status && status !== 'paid') {
          console.log('[PDFViewer] E. addStatusRibbon start');
          const stamped = await addStatusRibbon(pdfData as ArrayBuffer, status);
          if (stamped) pdfData = stamped;
          console.log('[PDFViewer] F. addStatusRibbon done');
        }

        console.log('[PDFViewer] G. pdfjsLib.getDocument start');
        const pdf = await pdfjsLib.getDocument({ data: pdfData }).promise;
        console.log('[PDFViewer] H. pdf loaded, numPages:', pdf.numPages);

        if (containerRef.current) {
          try {
            const page = await pdf.getPage(1);
            const nv = page.getViewport({ scale: 1 });
            const w = containerRef.current.clientWidth - 40;
            if (w > 0 && nv.width > 0)
              setZoom(Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round((w / nv.width) * 100))));
          } catch { /* keep default */ }
        }

        setPdfDoc(pdf);
        setNumPages(pdf.numPages);
        console.log('[PDFViewer] I. setPdfDoc done, rendering will start');
      } catch (err) {
        console.error('[PDFViewer] CRASH in loadPdf:', err);
        setError(t('components.PdfViewerNative.failedToLoadPdf'));
      }
    };
    loadPdf();
  }, [dataUrl, status, t]);

  // ── Render page: canvas + text layer + link layer ─────────────────────────
  const renderPage = useCallback(async (pageNum: number) => {
    if (!pdfDoc) return;
    const canvas = canvasRefs.current[pageNum];
    if (!canvas) return;

    const existingTask = renderTasksRef.current[pageNum];
    if (existingTask) { existingTask.cancel(); renderTasksRef.current[pageNum] = null; }
    const existingTL = textLayerInstanceRefs.current[pageNum];
    if (existingTL) { existingTL.cancel(); textLayerInstanceRefs.current[pageNum] = null; }
    const textLayerDiv = textLayerDivRefs.current[pageNum];
    if (textLayerDiv) textLayerDiv.innerHTML = '';

    try {
      const page = await pdfDoc.getPage(pageNum);
      const dpr = window.devicePixelRatio || 1;
      // Render ONCE at a fixed, oversampled resolution — NOT tied to `zoom`.
      // All zoom (pinch, buttons, wheel) is expressed purely as a CSS
      // transform: scale() on .pagesContainer (see the zoomWrapper JSX
      // below), so this never re-runs just because zoom changed. That's
      // deliberate: re-rasterizing on every zoom step is what caused the
      // pinch-zoom jank/scroll-snapping — this way there simply is no
      // re-render to race against during a gesture. OVERSAMPLE gives enough
      // headroom to CSS-scale up a couple hundred percent before it'd look
      // soft; capped dpr keeps per-page canvas memory reasonable on mobile.
      const OVERSAMPLE = 2;
      const effectiveDpr = Math.min(dpr, 2);
      const cssViewport    = page.getViewport({ scale: 1,                          rotation });
      const canvasViewport = page.getViewport({ scale: OVERSAMPLE * effectiveDpr,  rotation });

      console.log(`[PDFViewer] renderPage ${pageNum}: canvas ${canvasViewport.width}×${canvasViewport.height}px (dpr=${dpr}, natural)`);

      canvas.width  = canvasViewport.width;
      canvas.height = canvasViewport.height;
      canvas.style.width  = `${cssViewport.width}px`;
      canvas.style.height = `${cssViewport.height}px`;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const task = page.render({ canvasContext: ctx, viewport: canvasViewport, canvas });
      renderTasksRef.current[pageNum] = task;
      await task.promise;
      renderTasksRef.current[pageNum] = null;
      console.log(`[PDFViewer] renderPage ${pageNum}: canvas render done`);

      setRenderedPages(prev => new Set([...prev, pageNum]));

      // Pre-cache text content for highlight function to avoid stream conflicts
      if (!textContentCacheRef.current[pageNum]) {
        try {
          const stream = page.streamTextContent();
          const reader = stream.getReader();
          const items: any[] = [];
          try {
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
              if (value?.items) items.push(...value.items);
            }
          } finally {
            reader.releaseLock();
          }
          textContentCacheRef.current[pageNum] = { items };
        } catch (err) {
          textContentCacheRef.current[pageNum] = { items: [] };
        }
      }

      // Text layer
      if (textLayerDiv) {
        const tl = new TextLayer({
          textContentSource: page.streamTextContent(),
          container: textLayerDiv,
          viewport: cssViewport,
        });
        textLayerInstanceRefs.current[pageNum] = tl;
        await tl.render();
        textLayerInstanceRefs.current[pageNum] = null;
      }

      // Link annotation layer
      const linkDiv = annotationLayerDivRefs.current[pageNum];
      if (linkDiv) {
        linkDiv.innerHTML = '';
        try {
          const annotations = await page.getAnnotations({ intent: 'display' });
          for (const ann of annotations) {
            if (ann.subtype !== 'Link') continue;
            const [x1, y1, x2, y2] = cssViewport.convertToViewportRectangle(ann.rect);
            const el = document.createElement('a');
            el.style.left   = `${Math.min(x1, x2)}px`;
            el.style.top    = `${Math.min(y1, y2)}px`;
            el.style.width  = `${Math.abs(x2 - x1)}px`;
            el.style.height = `${Math.abs(y2 - y1)}px`;
            if (ann.url) {
              el.href = ann.url;
              el.target = '_blank';
              el.rel = 'noopener noreferrer';
            } else if (ann.dest) {
              el.href = '#';
              el.addEventListener('click', e => {
                e.preventDefault();
                if (Array.isArray(ann.dest) && ann.dest[0]?.num != null) {
                  pdfDoc.getPageIndex(ann.dest[0])
                    .then(idx => {
                      canvasRefs.current[idx + 1]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      setCurrentPage(idx + 1);
                    }).catch(() => {});
                }
              });
            } else continue;
            linkDiv.appendChild(el);
          }
        } catch { /* annotations optional */ }
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'RenderingCancelledException') return;
      console.error(`Error rendering page ${pageNum}:`, err);
    }
  }, [pdfDoc, rotation]);

  useEffect(() => {
    if (!pdfDoc || numPages === 0) return;
    // Deferred via a resolved-promise microtask (not called synchronously
    // in the effect body) — renderPage is itself async and its setState
    // calls happen well past this point regardless of this deferral.
    Promise.resolve().then(() => {
      for (let i = 1; i <= numPages; i++) renderPage(i);
    });
  }, [pdfDoc, numPages, renderPage]);

  // Track .pagesContainer's natural (untransformed) size — offsetWidth/Height
  // ignore CSS transforms entirely, so this only actually fires when the real
  // content changes (pages load, rotation flips dimensions), never from zoom.
  useEffect(() => {
    const pages = pagesContainerRef.current;
    if (!pages) return;
    const measure = () => setNaturalSize({ width: pages.offsetWidth, height: pages.offsetHeight });
    const ro = new ResizeObserver(measure);
    ro.observe(pages);
    measure();
    return () => ro.disconnect();
  }, [pdfDoc, numPages, rotation]);

  // ── Thumbnails ─────────────────────────────────────────────────────────────
  const renderThumbnail = useCallback(async (pageNum: number) => {
    if (!pdfDoc || thumbnails[pageNum]) return;
    try {
      const page = await pdfDoc.getPage(pageNum);
      const viewport = page.getViewport({ scale: 0.15 });
      const canvas = document.createElement('canvas');
      canvas.width = viewport.width; canvas.height = viewport.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      await page.render({ canvasContext: ctx, viewport, canvas }).promise;
      setThumbnails(prev => ({ ...prev, [pageNum]: canvas.toDataURL('image/png') }));
    } catch { /* ignore */ }
  }, [pdfDoc, thumbnails]);

  useEffect(() => {
    if (!showThumbnails || !pdfDoc) return;
    // Deferred via a resolved-promise microtask (not called synchronously
    // in the effect body) — renderThumbnail is itself async and its
    // setState calls happen well past this point regardless of this
    // deferral.
    Promise.resolve().then(() => {
      for (let i = 1; i <= Math.min(numPages, 20); i++) renderThumbnail(i);
    });
  }, [showThumbnails, pdfDoc, numPages, renderThumbnail]);

  // ── Page tracking ──────────────────────────────────────────────────────────
  const handleScroll = useCallback(() => {
    if (!containerRef.current || numPages <= 1) return;
    const top = containerRef.current.getBoundingClientRect().top;
    let best = 1, bestDist = Infinity;
    for (let i = 1; i <= numPages; i++) {
      const c = canvasRefs.current[i];
      if (!c) continue;
      const d = Math.abs(c.getBoundingClientRect().top - top);
      if (d < bestDist) { bestDist = d; best = i; }
    }
    setCurrentPage(best);
  }, [numPages]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // ── Zoom ───────────────────────────────────────────────────────────────────
  const clampZoom = (v: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, v));
  const handleZoomIn  = () => setZoom(z => clampZoom(z + ZOOM_STEP));
  const handleZoomOut = () => setZoom(z => clampZoom(z - ZOOM_STEP));
  const handleRotate  = () => setRotation(r => (r + 90) % 360);

  const handleFitWidth = useCallback(async () => {
    if (!pdfDoc || !containerRef.current) return;
    try {
      const page = await pdfDoc.getPage(1);
      const nv = page.getViewport({ scale: 1 });
      const w = containerRef.current.clientWidth - 40;
      if (w > 0 && nv.width > 0) setZoom(clampZoom(Math.round((w / nv.width) * 100)));
    } catch { /**/ }
  }, [pdfDoc]);

  useEffect(() => { onZoomChange?.(zoom); }, [zoom, onZoomChange]);
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      wheelDeltaRef.current += e.deltaY;
      const steps = Math.trunc(wheelDeltaRef.current / 50);
      if (steps === 0) return;
      wheelDeltaRef.current -= steps * 50;
      setZoom(z => clampZoom(z - steps * 5));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [pdfDoc]);

  // ── Navigation ─────────────────────────────────────────────────────────────
  const goToPage = (pageNum: number) => {
    const n = Math.min(Math.max(1, pageNum), numPages);
    setCurrentPage(n);
    canvasRefs.current[n]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setShowThumbnails(false);
  };

  // ── Fullscreen ─────────────────────────────────────────────────────────────
  const handleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      outerContainerRef.current?.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen().catch(() => {});
    }
  }, []);

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  // ── Download / Print / Share ───────────────────────────────────────────────
  // Build an annotated PDF bytes from the original + current highlights
  const buildAnnotatedPdfBytes = useCallback(async (): Promise<Uint8Array | null> => {
    if (!dataUrl || !pdfDoc) return null;
    try {
      let rawBytes: Uint8Array;
      if (dataUrl.startsWith('data:application/pdf;base64,')) {
        const b64 = dataUrl.replace('data:application/pdf;base64,', '');
        const bin = atob(b64);
        rawBytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) rawBytes[i] = bin.charCodeAt(i);
      } else {
        const ab = await fetch(dataUrl).then(r => r.arrayBuffer());
        rawBytes = new Uint8Array(ab);
      }

      const pdfLibDoc = await PDFDocument.load(rawBytes);
      const pages = pdfLibDoc.getPages();

      for (const hl of highlights) {
        const page = pages[hl.pageNum - 1];
        if (!page) continue;
        const { height: pageH } = page.getSize();

        const pxPerPt = hl.zoomAtSave / 100; // CSS px per PDF point at save time

        // Parse RGBA from color string e.g. "rgba(255,220,0,0.38)"
        const m = hl.color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/);
        const [r, g, b, a] = m
          ? [+m[1] / 255, +m[2] / 255, +m[3] / 255, +m[4]]
          : [1, 0.86, 0, 0.38];

        for (const rect of hl.rects) {
          // Convert CSS px (at zoomAtSave) → PDF points
          const ptX = rect.x / pxPerPt;
          const ptW = rect.w / pxPerPt;
          const ptH = rect.h / pxPerPt;
          // PDF Y origin is bottom-left; CSS Y origin is top-left
          const ptY = pageH - (rect.y / pxPerPt) - ptH;

          page.drawRectangle({
            x: ptX, y: ptY, width: ptW, height: ptH,
            color: rgb(r, g, b),
            opacity: a,
          });
        }
      }

      // Draw text notes
      const font = await pdfLibDoc.embedFont(StandardFonts.Helvetica);
      const fontSize = 9;
      const padding = 4;

      for (const note of notes) {
        if (!note.text.trim()) continue;
        const page = pages[note.pageNum - 1];
        if (!page) continue;
        const { height: pageH } = page.getSize();

        const pxPerPt = note.zoomAtSave / 100;

        const lines = note.text.split('\n').filter(l => l.length > 0);
        const boxW = 120;
        const boxH = padding * 2 + lines.length * (fontSize + 2);
        const ptX = note.x / pxPerPt;
        // CSS Y → PDF Y (flip + adjust so box sits below the click point)
        const ptY = pageH - (note.y / pxPerPt) - boxH;

        // Yellow background
        page.drawRectangle({
          x: ptX, y: ptY, width: boxW, height: boxH,
          color: rgb(1, 0.97, 0.7),
          opacity: 0.95,
          borderColor: rgb(0.7, 0.55, 0),
          borderWidth: 0.75,
        });

        // Text lines
        lines.forEach((line, li) => {
          page.drawText(line, {
            x: ptX + padding,
            y: ptY + boxH - padding - (li + 1) * (fontSize + 2) + 2,
            size: fontSize,
            font,
            color: rgb(0.15, 0.15, 0.15),
          });
        });
      }

      return await pdfLibDoc.save();
    } catch (e) {
      console.error('buildAnnotatedPdfBytes error', e);
      return null;
    }
  }, [dataUrl, pdfDoc, highlights, notes]);

  const handleDownload = useCallback(async () => {
    const bytes = await buildAnnotatedPdfBytes();
    if (!bytes) {
      // Fallback: download original
      if (!dataUrl) return;
      const a = document.createElement('a');
      a.href = dataUrl; a.download = fileName;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      return;
    }
    const blob = new Blob([bytes as BlobPart], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = fileName;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [buildAnnotatedPdfBytes, dataUrl, fileName]);

  const handlePrint = useCallback(async () => {
    if (!pdfDoc) return;
    // Render each page at 2× with highlights, then print via a hidden iframe.
    // Each page gets its own @page size so the browser never splits an image.
    const PRINT_SCALE = 2;

    // Collect {dataUrl, widthPt, heightPt} per page (natural PDF points)
    const pages: Array<{ dataUrl: string; widthPt: number; heightPt: number }> = [];

    for (let pageNum = 1; pageNum <= numPages; pageNum++) {
      const page = await pdfDoc.getPage(pageNum);
      const naturalVp = page.getViewport({ scale: 1 });
      const vp = page.getViewport({ scale: PRINT_SCALE });
      const offscreen = document.createElement('canvas');
      offscreen.width  = vp.width;
      offscreen.height = vp.height;
      const ctx = offscreen.getContext('2d')!;

      await page.render({ canvasContext: ctx, canvas: offscreen, viewport: vp }).promise;

      // Composite highlights on top
      const pageHls = highlights.filter(h => h.pageNum === pageNum);
      for (const hl of pageHls) {
        const s = PRINT_SCALE / (hl.zoomAtSave / 100);
        const m = hl.color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/);
        const [r, g, b, a] = m ? [+m[1], +m[2], +m[3], +m[4]] : [255, 220, 0, 0.38];
        ctx.save();
        ctx.globalCompositeOperation = 'multiply';
        ctx.fillStyle = `rgba(${r},${g},${b},${a})`;
        for (const rect of hl.rects) ctx.fillRect(rect.x * s, rect.y * s, rect.w * s, rect.h * s);
        ctx.restore();
      }

      // Composite text notes on top
      const pageNotes = notes.filter(n => n.pageNum === pageNum && n.text.trim());
      for (const note of pageNotes) {
        const s = PRINT_SCALE / (note.zoomAtSave / 100);
        const nx = note.x * s;
        const ny = note.y * s;
        const fontSize = 18 * (PRINT_SCALE / 1);
        const padding = 8 * (PRINT_SCALE / 1);
        const boxW = 240 * (PRINT_SCALE / 1);
        const lineH = fontSize + 4;
        const lines = note.text.split('\n');
        const boxH = padding * 2 + lines.length * lineH;

        ctx.save();
        ctx.globalCompositeOperation = 'source-over';
        // Background
        ctx.fillStyle = 'rgba(255, 248, 180, 0.95)';
        ctx.strokeStyle = 'rgba(180, 140, 0, 0.6)';
        ctx.lineWidth = 1.5 * PRINT_SCALE;
        ctx.beginPath();
        ctx.rect(nx, ny, boxW, boxH);
        ctx.fill();
        ctx.stroke();
        // Text
        ctx.fillStyle = '#333';
        ctx.font = `${fontSize}px sans-serif`;
        lines.forEach((line, li) => {
          ctx.fillText(line, nx + padding, ny + padding + (li + 1) * lineH - 4);
        });
        ctx.restore();
      }

      pages.push({
        dataUrl: offscreen.toDataURL('image/jpeg', 0.95),
        widthPt: naturalVp.width,
        heightPt: naturalVp.height,
      });
    }

    // Build HTML: one <section> per page, each with its own named @page size
    const pageStyles = pages.map((p, i) =>
      `@page p${i} { size: ${p.widthPt}pt ${p.heightPt}pt; margin: 0; }`
    ).join('\n');
    const pageDivs = pages.map((p, i) =>
      `<section class="p${i}"><img src="${p.dataUrl}" /></section>`
    ).join('\n');

    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;top:0;left:0;width:0;height:0;border:none;opacity:0;';
    document.body.appendChild(iframe);

    iframe.srcdoc = `<!DOCTYPE html><html><head><style>
      * { margin:0; padding:0; box-sizing:border-box; }
      ${pageStyles}
      ${pages.map((_, i) => `.p${i} { page: p${i}; }`).join('\n')}
      section {
        display: block;
        break-before: page;
        break-after: page;
        break-inside: avoid;
        overflow: hidden;
      }
      section:first-child { break-before: auto; }
      img { display:block; width:100%; height:100%; object-fit:contain; }
    </style></head><body>${pageDivs}</body></html>`;

    iframe.addEventListener('load', () => {
      const doc = iframe.contentDocument!;
      const allImgs = doc.querySelectorAll('img');
      let loaded = 0;
      const tryPrint = () => {
        if (++loaded >= allImgs.length) {
          iframe.contentWindow!.focus();
          iframe.contentWindow!.print();
          setTimeout(() => document.body.removeChild(iframe), 1000);
        }
      };
      if (allImgs.length === 0) { tryPrint(); return; }
      allImgs.forEach(img => {
        if ((img as HTMLImageElement).complete) tryPrint();
        else img.addEventListener('load', tryPrint, { once: true });
      });
    }, { once: true });
  }, [pdfDoc, numPages, highlights, notes]);

  const handleShare = useCallback(async () => {
    if (!('share' in navigator)) return;
    try {
      const bytes = await buildAnnotatedPdfBytes();
      const blob = bytes
        ? new Blob([bytes as BlobPart], { type: 'application/pdf' })
        : await fetch(dataUrl!).then(r => r.blob());
      await navigator.share({ files: [new File([blob], fileName, { type: 'application/pdf' })], title: fileName });
    } catch { /**/ }
  }, [buildAnnotatedPdfBytes, dataUrl, fileName]);

  // ── Undo ───────────────────────────────────────────────────────────────────
  const undoAnnotation = useCallback(() => {
    setAnnotationHistory(prev => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      if (last.type === 'highlight') setHighlights(h => h.filter(x => x.id !== last.id));
      else setNotes(n => n.filter(x => x.id !== last.id));
      return prev.slice(0, -1);
    });
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        undoAnnotation();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [undoAnnotation]);

  // ── Text notes ─────────────────────────────────────────────────────────────
  const handlePageClick = useCallback((e: React.MouseEvent, pageNum: number) => {
    if (viewMode !== 'note') return;
    e.stopPropagation();
    const container = pageContainerRefs.current[pageNum];
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setNotes(prev => [...prev, {
      id: Math.random().toString(36).slice(2),
      pageNum, x, y,
      width: 180, height: 100,
      text: '', editing: true, zoomAtSave: zoom,
    }]);
  }, [viewMode, zoom]);

  const updateNote = (id: string, text: string) =>
    setNotes(prev => prev.map(n => n.id === id ? { ...n, text } : n));

  const commitNote = (id: string) =>
    setNotes(prev => prev.flatMap(n => {
      if (n.id !== id) return [n];
      if (!n.text.trim()) return [];
      setAnnotationHistory(h => [...h, { type: 'note', id }]);
      return [{ ...n, editing: false }];
    }));

  const deleteNote = (id: string) =>
    setNotes(prev => prev.filter(n => n.id !== id));

  const editNote = (id: string) =>
    setNotes(prev => prev.map(n => n.id === id ? { ...n, editing: true } : n));

  const updateNoteSize = (id: string, screenW: number, screenH: number, scale: number) =>
    setNotes(prev => prev.map(n => n.id === id
      ? { ...n, width: screenW / scale, height: screenH / scale }
      : n));

  const startNoteDrag = (e: React.MouseEvent, note: TextNote) => {
    e.stopPropagation();
    e.preventDefault();
    // Natural (100%)-relative, not current-zoom-relative — .pagesContainer's
    // own CSS transform now applies the live zoom automatically to
    // everything inside it (including this note), so saved positions only
    // need converting back to the natural scale they're stored/rendered in.
    const ns = 100 / note.zoomAtSave;
    noteDragRef.current = {
      id: note.id,
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      startNoteX: note.x * ns,
      startNoteY: note.y * ns,
      scale: ns,
    };
  };

  // Global mousemove/mouseup for note dragging
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const d = noteDragRef.current;
      if (!d) return;
      const dx = e.clientX - d.startMouseX;
      const dy = e.clientY - d.startMouseY;
      setNotes(prev => prev.map(n => n.id === d.id
        ? { ...n, x: (d.startNoteX + dx) / d.scale, y: (d.startNoteY + dy) / d.scale }
        : n));
    };
    const onUp = () => { noteDragRef.current = null; };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, []);

  // ── Touch: pinch-zoom + 2D pan, both fully JS-driven ────────────────────────
  // touch-action: none on .content (see PdfViewerNative.module.scss) hands ALL
  // touch gesture interpretation to this listener — no native scroll/pan at
  // all. That's deliberate: letting the browser natively claim panning (via
  // pan-x/pan-y touch-action) raced against this component's own 2-finger
  // pinch handling on Android WebView, silently breaking pinch-zoom. Doing
  // both gestures ourselves removes that ambiguity entirely.
  //
  // setZoom() is now cheap — renderPage() no longer depends on zoom at all
  // (see above), so this just updates a CSS transform, not a re-render.
  // That means pinch can update zoom continuously, every touchmove, same as
  // any other live gesture; no separate "live preview vs. commit" split
  // needed anymore. Anchoring the pinch midpoint (so content stays under
  // the fingers) is handled by the useLayoutEffect below, which runs
  // synchronously right after the zoom-driven resize lands in the DOM.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        touchPanRef.current = null;
        const t0 = e.touches[0], t1 = e.touches[1];
        const dx = t0.clientX - t1.clientX;
        const dy = t0.clientY - t1.clientY;
        // Fixed baseline for this gesture — every touchmove below compares
        // back to THIS starting distance/zoom (absolute), not the previous
        // frame's (compounding), so noisy/duplicate frames can't drift the
        // result permanently.
        pinchDistRef.current = Math.sqrt(dx * dx + dy * dy);
        pinchStartZoomRef.current = zoomRef.current;
        const midX = (t0.clientX + t1.clientX) / 2;
        const midY = (t0.clientY + t1.clientY) / 2;
        const contentRect = el.getBoundingClientRect();
        const localX = midX - contentRect.left;
        const localY = midY - contentRect.top;
        pendingPinchAnchorRef.current = {
          contentX: el.scrollLeft + localX,
          contentY: el.scrollTop  + localY,
          localX, localY,
          ratio: 1,
        };
      } else if (e.touches.length === 1) {
        pinchDistRef.current = null;
        const touch = e.touches[0];

        // Highlight mode — mirrors handleMouseDown's highlight branch below.
        // A 1-finger touch here draws a selection box instead of panning.
        if (viewMode === 'highlight') {
          for (let pageNum = 1; pageNum <= numPages; pageNum++) {
            const container = pageContainerRefs.current[pageNum];
            if (!container) continue;
            const bcr = container.getBoundingClientRect();
            if (touch.clientX >= bcr.left && touch.clientX <= bcr.right && touch.clientY >= bcr.top && touch.clientY <= bcr.bottom) {
              const x = touch.clientX - bcr.left;
              const y = touch.clientY - bcr.top;
              hlDragStartRef.current = { pageNum, x, y };
              setHlDrag({ pageNum, x, y, w: 0, h: 0 });
              touchPanRef.current = null;
              return;
            }
          }
        }

        touchPanRef.current = { x: touch.clientX, y: touch.clientY };
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2 && pinchDistRef.current !== null) {
        e.preventDefault();
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const nextZoom = clampZoom(Math.round(pinchStartZoomRef.current * (dist / pinchDistRef.current)));
        zoomRef.current = nextZoom;
        if (pendingPinchAnchorRef.current) {
          pendingPinchAnchorRef.current.ratio = nextZoom / pinchStartZoomRef.current;
        }
        setZoom(nextZoom);
        return;
      }
      if (e.touches.length !== 1) return;
      const touch = e.touches[0];

      if (hlDragStartRef.current) {
        e.preventDefault();
        const start = hlDragStartRef.current;
        const container = pageContainerRefs.current[start.pageNum];
        if (container) {
          const bcr = container.getBoundingClientRect();
          const curX = touch.clientX - bcr.left;
          const curY = touch.clientY - bcr.top;
          setHlDrag({
            pageNum: start.pageNum,
            x: Math.min(start.x, curX),
            y: Math.min(start.y, curY),
            w: Math.abs(curX - start.x),
            h: Math.abs(curY - start.y),
          });
        }
        return;
      }

      if (touchPanRef.current) {
        e.preventDefault();
        el.scrollLeft -= touch.clientX - touchPanRef.current.x;
        el.scrollTop  -= touch.clientY - touchPanRef.current.y;
        touchPanRef.current = { x: touch.clientX, y: touch.clientY };
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      pinchDistRef.current = null;
      pendingPinchAnchorRef.current = null;

      if (hlDragStartRef.current) {
        const start = hlDragStartRef.current;
        const touch = e.changedTouches[0];
        const container = pageContainerRefs.current[start.pageNum];
        if (touch && container) {
          const bcr = container.getBoundingClientRect();
          const curX = touch.clientX - bcr.left;
          const curY = touch.clientY - bcr.top;
          const rect = {
            x: Math.min(start.x, curX),
            y: Math.min(start.y, curY),
            w: Math.abs(curX - start.x),
            h: Math.abs(curY - start.y),
          };
          if (rect.w > 5 && rect.h > 5) applyHighlightFromDragRef.current?.(start.pageNum, rect);
        }
        hlDragStartRef.current = null;
        setHlDrag(null);
      }

      if (!e.touches.length) touchPanRef.current = null;
    };

    el.addEventListener('touchstart',  onTouchStart, { passive: true });
    el.addEventListener('touchmove',   onTouchMove,  { passive: false });
    el.addEventListener('touchend',    onTouchEnd,   { passive: true });
    el.addEventListener('touchcancel', onTouchEnd,   { passive: true });

    return () => {
      el.removeEventListener('touchstart',  onTouchStart);
      el.removeEventListener('touchmove',   onTouchMove);
      el.removeEventListener('touchend',    onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  // Re-run once pdfDoc loads and containerRef's element actually mounts —
  // on the very first render (isLoading===true) this component renders a
  // different placeholder tree with no containerRef div at all, so an
  // empty dep array here would attach to nothing, permanently (matches the
  // fix already used by the wheel-zoom effect above, same root cause).
  // viewMode is included so switching tools mid-session is picked up —
  // applyHighlightFromDrag is intentionally NOT here (see
  // applyHighlightFromDragRef's comment) since it's declared later in this
  // component and would be a forward reference in this dependency array.
  }, [pdfDoc, viewMode, numPages]);

  // ── Pinch scroll-anchoring ───────────────────────────────────────────────
  // zoom is now purely a CSS transform (see the zoomWrapper JSX below), so
  // the resize is synchronous with React's own commit — useLayoutEffect runs
  // right after DOM mutations land but before paint, which is exactly when
  // el.scrollWidth/Height already reflect the new zoom. No ResizeObserver or
  // timing guesswork needed (that was only ever necessary because the old
  // architecture's resize was async pdf.js re-rendering).
  useLayoutEffect(() => {
    const anchor = pendingPinchAnchorRef.current;
    if (!anchor) return;
    const el = containerRef.current;
    if (!el) return;
    el.scrollLeft = anchor.contentX * anchor.ratio - anchor.localX;
    el.scrollTop  = anchor.contentY * anchor.ratio - anchor.localY;
  }, [zoom]);

  // ── Drag-to-pan ────────────────────────────────────────────────────────────
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;

    if (viewMode === 'pan') {
      e.preventDefault();
      isDraggingRef.current = true;
      dragStartRef.current = { x: e.clientX, y: e.clientY };
      setIsDragging(true);
      return;
    }

    if (viewMode === 'highlight') {
      for (let pageNum = 1; pageNum <= numPages; pageNum++) {
        const container = pageContainerRefs.current[pageNum];
        if (!container) continue;
        const bcr = container.getBoundingClientRect();
        if (e.clientX >= bcr.left && e.clientX <= bcr.right && e.clientY >= bcr.top && e.clientY <= bcr.bottom) {
          e.preventDefault();
          const x = e.clientX - bcr.left;
          const y = e.clientY - bcr.top;
          hlDragStartRef.current = { pageNum, x, y };
          setHlDrag({ pageNum, x, y, w: 0, h: 0 });
          return;
        }
      }
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDraggingRef.current && containerRef.current) {
      e.preventDefault();
      const dx = e.clientX - dragStartRef.current.x;
      const dy = e.clientY - dragStartRef.current.y;
      containerRef.current.scrollLeft -= dx;
      containerRef.current.scrollTop  -= dy;
      dragStartRef.current = { x: e.clientX, y: e.clientY };
      return;
    }

    const start = hlDragStartRef.current;
    if (!start) return;
    const container = pageContainerRefs.current[start.pageNum];
    if (!container) return;
    const bcr = container.getBoundingClientRect();
    const curX = e.clientX - bcr.left;
    const curY = e.clientY - bcr.top;
    setHlDrag({
      pageNum: start.pageNum,
      x: Math.min(start.x, curX),
      y: Math.min(start.y, curY),
      w: Math.abs(curX - start.x),
      h: Math.abs(curY - start.y),
    });
  };

  const stopDrag = () => { isDraggingRef.current = false; setIsDragging(false); };

  // ── Drag-to-area highlight — snaps to text lines inside the drag rect ──────
  const applyHighlightFromDrag = useCallback(async (
    pageNum: number,
    drag: { x: number; y: number; w: number; h: number },
  ) => {
    if (!pdfDoc) return;
    try {
      const page = await pdfDoc.getPage(pageNum);
      const cssScale = zoom / 100;
      const viewport = page.getViewport({ scale: cssScale, rotation });
      
      // Use cached text content to avoid stream conflicts with TextLayer
      let textContent = textContentCacheRef.current[pageNum];
      if (!textContent) {
        textContent = { items: [] };
      }

      const rawRects: Array<{ x: number; y: number; w: number; h: number }> = [];

      for (const rawItem of textContent.items) {
        const item = rawItem as { str: string; width: number; height: number; transform: number[] };
        if (!item.str.trim() || !item.transform) continue;

        const [, , , , tx, ty] = item.transform;
        const [vx, vy] = viewport.convertToViewportPoint(tx, ty);
        const iw = item.width  * cssScale;
        // EM height includes ascent + descent. vy = baseline.
        // Visible text tops out at vy - ascent; extend below baseline for
        // sub-pixel stroke spillover (≈15% of EM for typical Latin fonts).
        const ih    = item.height * cssScale;
        const descExtra = ih * 0.15;
        const rx = vx;
        const ry = vy - ih;
        const rh = ih + descExtra;

        // Skip items with no overlap
        if (rx + iw < drag.x || rx > drag.x + drag.w ||
            ry + rh < drag.y || ry > drag.y + drag.h) continue;

        rawRects.push({ x: rx, y: ry, w: iw, h: rh });
      }

      if (rawRects.length === 0) return;

      const rects = mergeRects(rawRects);
      const id = Math.random().toString(36).slice(2);
      setHighlights(prev => [...prev, { id, pageNum, rects, color: activeHlColor, zoomAtSave: zoom }]);
      setAnnotationHistory(prev => [...prev, { type: 'highlight', id }]);
    } catch (err) {
      console.error('applyHighlightFromDrag:', err);
    }
  }, [pdfDoc, zoom, rotation, activeHlColor]);

  useEffect(() => { applyHighlightFromDragRef.current = applyHighlightFromDrag; }, [applyHighlightFromDrag]);

  const handleMouseUp = (_e: React.MouseEvent) => {
    const start = hlDragStartRef.current;
    if (start && hlDrag && hlDrag.w > 5 && hlDrag.h > 5)
      applyHighlightFromDrag(hlDrag.pageNum, hlDrag);
    hlDragStartRef.current = null;
    setHlDrag(null);
    stopDrag();
  };

  // ── Toolbar responsive breakpoints (driven by ResizeObserver) ───────────────
  // Re-run once pdfDoc loads and toolbarRef's element actually mounts — same
  // root cause as the touch-listener and natural-size effects above: on the
  // very first render (isLoading===true) this component renders a different
  // placeholder tree with no toolbar at all, so an empty dep array here
  // attached to nothing, permanently. tbWidth was stuck at its hardcoded
  // default (800) forever, so isNarrow/isMedium never actually triggered —
  // every toolbar button always rendered inline regardless of real screen
  // width, which is why they overflowed off narrow phone screens.
  useEffect(() => {
    const el = toolbarRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setTbWidth(entry.contentRect.width));
    ro.observe(el);
    setTbWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, [pdfDoc]);

  // Keep page input in sync with scroll-driven currentPage changes
  useEffect(() => {
    const el = pageInputRef.current;
    if (el && document.activeElement !== el) el.value = String(currentPage);
  }, [currentPage]);

  // ── Render ─────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingState}>
          <IonSpinner name="bubbles" color="primary" />
          <span>{t('components.PdfViewerNative.loadingPdf')}</span>
        </div>
      </div>
    );
  }

  if (error || !dataUrl) {
    return (
      <div className={styles.container}>
        <div className={styles.errorState}>
          <IonIcon icon={alertCircleOutline} />
          <span>{error || t('components.PdfViewerNative.pdfUnavailable')}</span>
        </div>
      </div>
    );
  }

  // Raised from the original 460/600 — buttons grew from 28px to 44px touch
  // targets, so the same pixel width now fits noticeably fewer of them.
  const isNarrow = tbWidth < 520;
  const isMedium = tbWidth < 660;

  return (
    <div ref={outerContainerRef} className={styles.container}>
      <div ref={toolbarRef} className={styles.toolbar}>

        {/* ── LEFT: thumbnails + tools ── */}
        <div className={styles.tbLeft}>
          <button className={`${styles.toolbarButton} ${showThumbnails ? styles.active : ''}`}
            onClick={() => setShowThumbnails(v => !v)} title={t('components.PdfViewerNative.pageThumbnails')}>
            <IonIcon icon={gridOutline} />
          </button>

          <div className={styles.tbMenuWrap}>
            <button
              className={`${styles.toolbarButton} ${styles.hlDrawBtn} ${viewMode !== 'select' ? styles.active : ''}`}
              onClick={() => { setToolsMenuOpen(v => !v); setMoreMenuOpen(false); }}
              title={t('components.PdfViewerNative.tools')}>
              <IonIcon icon={
                viewMode === 'pan' ? handLeftOutline :
                viewMode === 'note' ? chatbubbleOutline : pencilOutline
              } />
              {viewMode === 'highlight' && (
                <span className={styles.hlColorBar} style={{ background: activeHlColor.replace(/[\d.]+\)$/, '1)') }} />
              )}
            </button>

            {toolsMenuOpen && (
              <div className={styles.toolsDropdown} onMouseLeave={() => setToolsMenuOpen(false)}>
                <button className={`${styles.toolsItem} ${viewMode === 'select' ? styles.active : ''}`}
                  onClick={() => { setViewMode('select'); setToolsMenuOpen(false); }}>
                  <IonIcon icon={textOutline} /><span>{t('components.PdfViewerNative.selectText')}</span>
                </button>

                <button className={`${styles.toolsItem} ${viewMode === 'highlight' ? styles.active : ''}`}
                  onClick={() => setViewMode('highlight')}>
                  <IonIcon icon={pencilOutline} /><span>{t('components.PdfViewerNative.highlight')}</span>
                  <span className={styles.hlMenuDot} style={{ background: activeHlColor.replace(/[\d.]+\)$/, '1)') }} />
                </button>
                <div className={styles.hlMenuColors}>
                  {HL_COLORS.map(color => (
                    <button key={color}
                      className={`${styles.hlColorBtn} ${color === activeHlColor ? styles.hlColorActive : ''}`}
                      style={{ background: color.replace(/[\d.]+\)$/, '1)') }}
                      onClick={() => setActiveHlColor(color)} />
                  ))}
                </div>

                <button className={styles.toolsItem}
                  onClick={() => { undoAnnotation(); setToolsMenuOpen(false); }}
                  disabled={annotationHistory.length === 0}>
                  <IonIcon icon={arrowUndoOutline} /><span>{t('components.PdfViewerNative.eraseLast')}</span>
                </button>

                <button className={`${styles.toolsItem} ${viewMode === 'pan' ? styles.active : ''}`}
                  onClick={() => { setViewMode('pan'); setToolsMenuOpen(false); }}>
                  <IonIcon icon={handLeftOutline} /><span>{t('components.PdfViewerNative.pan')}</span>
                </button>

                <button className={`${styles.toolsItem} ${viewMode === 'note' ? styles.active : ''}`}
                  onClick={() => { setViewMode('note'); setToolsMenuOpen(false); }}>
                  <IonIcon icon={chatbubbleOutline} /><span>{t('components.PdfViewerNative.addTextNote')}</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ── CENTER: zoom + page input ── */}
        <div className={styles.tbMid}>
          <button className={styles.toolbarButton} onClick={handleZoomOut} disabled={zoom <= MIN_ZOOM} title={t('components.PdfViewerNative.zoomOut')}>
            <IonIcon icon={removeOutline} />
          </button>
          <button className={styles.toolbarButton} onClick={handleZoomIn} disabled={zoom >= MAX_ZOOM} title={t('components.PdfViewerNative.zoomIn')}>
            <IonIcon icon={addOutline} />
          </button>
          {!isNarrow && (
            <button className={styles.toolbarButton} onClick={handleFitWidth} title={t('components.PdfViewerNative.fitWidth')}>
              <IonIcon icon={contractOutline} />
            </button>
          )}

          {numPages > 0 && (
            <>
              <span className={styles.tbDivider} />
              <input
                ref={pageInputRef}
                className={styles.pageInput}
                defaultValue={currentPage}
                onBlur={e => {
                  const v = parseInt((e.target as HTMLInputElement).value, 10);
                  if (!isNaN(v)) goToPage(v);
                  else (e.target as HTMLInputElement).value = String(currentPage);
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') { const v = parseInt(e.currentTarget.value, 10); if (!isNaN(v)) goToPage(v); e.currentTarget.blur(); }
                  if (e.key === 'Escape') { e.currentTarget.value = String(currentPage); e.currentTarget.blur(); }
                }}
                type="text" inputMode="numeric" pattern="[0-9]*"
              />
              <span className={styles.pageOf}>{t('components.PdfViewerNative.of', { numPages })}</span>
            </>
          )}
        </div>

        {/* ── RIGHT: progressive actions + overflow menu ── */}
        <div className={styles.tbRight}>
          {/* Download — inline at medium+ */}
          {!isNarrow && (
            <button className={styles.toolbarButton} onClick={handleDownload} title={t('components.PdfViewerNative.saveAs')}>
              <IonIcon icon={downloadOutline} />
            </button>
          )}
          {/* Print — inline at wide+ */}
          {!isMedium && (
            <button className={styles.toolbarButton} onClick={handlePrint} title={t('components.PdfViewerNative.print')}>
              <IonIcon icon={printOutline} />
            </button>
          )}

          <div className={styles.moreMenu}>
            <button className={styles.toolbarButton}
              onClick={() => { setMoreMenuOpen(v => !v); setToolsMenuOpen(false); }}
              title={t('components.PdfViewerNative.more')}>
              <IonIcon icon={ellipsisHorizontal} />
            </button>
            {moreMenuOpen && (
              <div className={styles.moreDropdown} onMouseLeave={() => setMoreMenuOpen(false)}>
                {/* Only in menu when not shown inline */}
                {isNarrow && (
                  <button className={styles.moreItem} onClick={() => { handleFitWidth(); setMoreMenuOpen(false); }}>
                    <IonIcon icon={contractOutline} />{t('components.PdfViewerNative.fitWidth')}
                  </button>
                )}
                {isNarrow && (
                  <button className={styles.moreItem} onClick={() => { handleDownload(); setMoreMenuOpen(false); }}>
                    <IonIcon icon={downloadOutline} />{t('components.PdfViewerNative.saveAs')}
                  </button>
                )}
                {isMedium && (
                  <button className={styles.moreItem} onClick={() => { handlePrint(); setMoreMenuOpen(false); }}>
                    <IonIcon icon={printOutline} />{t('components.PdfViewerNative.print')}
                  </button>
                )}
                {/* Always in menu */}
                <button className={styles.moreItem} onClick={() => { handleFullscreen(); setMoreMenuOpen(false); }}>
                  <IonIcon icon={isFullscreen ? contractOutline : expandOutline} />
                  {isFullscreen ? t('components.PdfViewerNative.exitFullScreen') : t('components.PdfViewerNative.fullScreen')}
                </button>
                <button className={styles.moreItem} onClick={() => { handleRotate(); setMoreMenuOpen(false); }}>
                  <IonIcon icon={refreshOutline} />{t('components.PdfViewerNative.rotate')}
                </button>
                {'share' in navigator && (
                  <button className={styles.moreItem} onClick={() => { handleShare(); setMoreMenuOpen(false); }}>
                    <IonIcon icon={shareOutline} />{t('components.PdfViewerNative.share')}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Thumbnail panel */}
      {showThumbnails && (
        <div className={styles.thumbnailPanel}>
          <div className={styles.thumbnailHeader}>
            <span>{t('components.PdfViewerNative.pages')}</span>
            <button className={styles.closeThumbnails} onClick={() => setShowThumbnails(false)}>
              <IonIcon icon={closeOutline} />
            </button>
          </div>
          <div className={styles.thumbnailGrid}>
            {Array.from({ length: numPages }, (_, i) => (
              <div
                key={i + 1}
                className={`${styles.thumbnail} ${currentPage === i + 1 ? styles.active : ''}`}
                onClick={() => goToPage(i + 1)}
                title={t('components.PdfViewerNative.page', { v1: i + 1 })}
              >
                {thumbnails[i + 1]
                  ? <img src={thumbnails[i + 1]} alt={t('components.PdfViewerNative.page', { v1: i + 1 })} />
                  : <div className={styles.thumbnailLoading}><span>{i + 1}</span></div>
                }
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Scrollable page area */}
      <div
        ref={containerRef}
        className={`${styles.content} ${isDragging ? styles.dragging : viewMode === 'pan' ? styles.panMode : viewMode === 'highlight' ? styles.highlightMode : ''}`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={stopDrag}
      >
        <div
          className={styles.zoomWrapper}
          style={naturalSize ? {
            width:  naturalSize.width  * zoom / 100,
            height: naturalSize.height * zoom / 100,
          } : undefined}
        >
        <div
          ref={pagesContainerRef}
          className={styles.pagesContainer}
          style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'top left' }}
        >
          {Array.from({ length: numPages }, (_, i) => (
            <div
              key={i + 1}
              ref={el => { pageContainerRefs.current[i + 1] = el; }}
              className={`${styles.pageContainer} ${viewMode === 'note' ? styles.noteCursor : ''}`}
              style={{ display: renderedPages.has(i + 1) ? 'flex' : 'none' }}
              onClick={e => handlePageClick(e, i + 1)}
            >
              <canvas ref={el => { canvasRefs.current[i + 1] = el; }} className={styles.pageCanvas} data-page={i + 1} />

              {/* Highlight layer */}
              <div className={styles.highlightLayer}>
                {highlights
                  .filter(h => h.pageNum === i + 1)
                  .flatMap(h => {
                    // Natural-scale, not current-zoom-scale — see the
                    // startNoteDrag comment above for why.
                    const s = 100 / h.zoomAtSave;
                    return h.rects.map((r, ri) => (
                      <div key={`${h.id}-${ri}`} className={styles.highlightRect}
                        style={{ left: r.x * s, top: r.y * s, width: r.w * s, height: r.h * s, background: h.color }} />
                    ));
                  })}
                {hlDrag && hlDrag.pageNum === i + 1 && (() => {
                  // hlDrag.x/y/w/h are captured in current-zoom-scaled
                  // pixels (bcr-relative) — applyHighlightFromDrag needs
                  // that to match against its own zoom-scaled text
                  // viewport. But this preview box renders inside
                  // .pageContainer, whose content is natural-scale (the
                  // ancestor .pagesContainer applies the zoom transform),
                  // so it needs the same natural-scale conversion as saved
                  // highlights (s = 100/zoomAtSave above) — here "zoomAtSave"
                  // is just the current zoom, since this drag is live.
                  const ds = 100 / zoom;
                  return (
                    <div className={styles.highlightDragPreview}
                      style={{ left: hlDrag.x * ds, top: hlDrag.y * ds, width: hlDrag.w * ds, height: hlDrag.h * ds, background: activeHlColor }} />
                  );
                })()}
              </div>

              {/* Text note overlays */}
              {notes.filter(n => n.pageNum === i + 1).map(note => {
                const ns = 100 / note.zoomAtSave; // natural-scale, see comment above
                return (
                  <div
                    key={note.id}
                    className={styles.noteOverlay}
                    style={{
                      left: note.x * ns,
                      top: note.y * ns,
                      width:  note.editing ? note.width  * ns : 'fit-content',
                      height: note.editing ? note.height * ns : 'fit-content',
                      maxWidth: note.editing ? undefined : '220px',
                      resize: note.editing ? 'both' : 'none',
                    }}
                    onClick={e => e.stopPropagation()}
                    onMouseUp={e => {
                      if (!note.editing) return;
                      const el = e.currentTarget;
                      updateNoteSize(note.id, el.offsetWidth, el.offsetHeight, ns);
                    }}
                  >
                    {note.editing ? (
                      <>
                        {/* Drag handle — edit mode only */}
                        <div
                          className={styles.noteDragHandle}
                          onMouseDown={e => startNoteDrag(e, note)}
                        >
                          <span>≡</span>
                          <button
                            className={styles.noteDelete}
                            onMouseDown={e => { e.stopPropagation(); e.preventDefault(); deleteNote(note.id); }}
                            title={t('components.PdfViewerNative.deleteNote')}
                          >
                            <IonIcon icon={closeCircleOutline} />
                          </button>
                        </div>
                        <textarea
                          className={styles.noteTextarea}
                          value={note.text}
                          autoFocus
                          placeholder={t('components.PdfViewerNative.typeNote')}
                          onChange={e => updateNote(note.id, e.target.value)}
                          onKeyDown={e => { if (e.key === 'Escape') commitNote(note.id); }}
                          onBlur={() => commitNote(note.id)}
                        />
                      </>
                    ) : (
                      /* Read mode — plain text, double-click to edit */
                      <div className={styles.noteBody} onDoubleClick={() => editNote(note.id)}>
                        <span>{note.text}</span>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Text layer — active in select mode for copy; off in highlight/pan/note */}
              <div
                ref={el => { textLayerDivRefs.current[i + 1] = el; }}
                className="textLayer"
                style={{
                  pointerEvents: viewMode === 'select' ? 'auto' : 'none',
                  userSelect:    viewMode === 'select' ? 'text'  : 'none',
                }}
              />

              {/* Link overlay — z-index 3, always interactive */}
              <div
                ref={el => { annotationLayerDivRefs.current[i + 1] = el; }}
                className={styles.linkLayer}
              />
            </div>
          ))}
        </div>
        </div>
      </div>

    </div>
  );
};

export default PdfViewerNative;
